import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ImageGallery from './components/ImageGallery';
import TourHeader from './components/TourHeader';
import BookingWidget from './components/BookingWidget';
import TourItinerary from './components/TourItinerary';
import ReviewsSection from './components/ReviewsSection';
import TourInclusions from './components/TourInclusions';
import SimilarTours from './components/SimilarTours';
import Icon from '../../components/AppIcon';

const TourDetail = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const tourId = searchParams?.get('id') || '1';

  // Mock tour data
  const tourData = {
    id: tourId,
    name: "Dubai Desert Safari with BBQ Dinner & Cultural Show",
    destination: "Dubai",
    location: "Dubai Desert Conservation Reserve",
    category: "Adventure & Outdoor",
    duration: "6 hours",
    maxGroupSize: 15,
    rating: 4.8,
    reviewCount: 2847,
    totalBookings: 15420,
    languages: ["English", "Arabic", "Hindi"],
    cancellationPolicy: "Free",
    difficulty: "Easy",
    pricePerAdult: 89,
    pricePerChild: 65,
    originalPrice: 120,
    isPopular: true,
    instantBooking: true,
    images: [
      {
        url: "https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=800&h=600&fit=crop",
        caption: "Desert dune bashing adventure"
      },
      {
        url: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop",
        caption: "Traditional Bedouin camp"
      },
      {
        url: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&h=600&fit=crop",
        caption: "Camel riding experience"
      },
      {
        url: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop",
        caption: "Cultural dance performance"
      },
      {
        url: "https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=800&h=600&fit=crop",
        caption: "Desert sunset views"
      }
    ]
  };

  const itineraryData = [
    {
      title: "Hotel Pickup & Desert Drive",
      duration: "1 hour",
      locations: ["Dubai City", "Desert Conservation Reserve"],
      highlights: ["Air-conditioned 4WD vehicle", "Professional safari guide", "Safety briefing"],
      description: `Begin your desert adventure with a comfortable pickup from your Dubai hotel in a modern 4WD vehicle. Our experienced safari guide will provide a comprehensive safety briefing as we drive towards the Dubai Desert Conservation Reserve, sharing fascinating insights about the desert ecosystem and local wildlife along the way.`,
      timeline: [
        {
          time: "3:00 PM",
          duration: "30 mins",
          title: "Hotel Pickup",
          description: "Comfortable pickup from your Dubai hotel or designated meeting point",
          tips: "Be ready 10 minutes before pickup time. Bring sunglasses and a hat for sun protection."
        },
        {
          time: "3:30 PM",
          duration: "30 mins",
          title: "Desert Drive",
          description: "Scenic drive to the desert conservation reserve with photo stops",
          image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop"
        }
      ],
      transportation: "Air-conditioned 4WD vehicle with professional driver"
    },
    {
      title: "Dune Bashing Adventure",
      duration: "45 minutes",
      locations: ["Red Dunes", "Big Red Dune"],
      highlights: ["Thrilling dune bashing", "Photo opportunities", "Desert wildlife spotting"],
      description: `Experience the ultimate adrenaline rush with our signature dune bashing adventure. Our skilled drivers will navigate the towering red dunes, creating an exhilarating roller-coaster experience across the desert landscape. Multiple photo stops are included to capture the breathtaking desert scenery and wildlife.`,
      timeline: [
        {
          time: "4:00 PM",
          duration: "30 mins",
          title: "Dune Bashing",
          description: "Thrilling ride across the red dunes with experienced drivers",
          image: "https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=400&h=300&fit=crop",
          tips: "Hold on tight and enjoy the ride! Those prone to motion sickness should sit in the front seat."
        },
        {
          time: "4:30 PM",
          duration: "15 mins",
          title: "Photo Stop",
          description: "Capture stunning desert landscapes and wildlife sightings",
          tips: "Golden hour lighting provides the best photography opportunities."
        }
      ]
    },
    {
      title: "Bedouin Camp Experience",
      duration: "3.5 hours",
      locations: ["Traditional Bedouin Camp"],
      highlights: ["Camel riding", "Henna painting", "Shisha smoking", "Cultural performances"],
      description: `Immerse yourself in authentic Bedouin culture at our traditional desert camp. Enjoy camel riding, get beautiful henna designs, try traditional shisha, and witness captivating cultural performances including belly dancing and Tanoura shows while savoring a delicious BBQ dinner under the stars.`,
      timeline: [
        {
          time: "5:00 PM",
          duration: "30 mins",
          title: "Camp Arrival & Activities",
          description: "Welcome drinks and traditional camp activities setup",
          image: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400&h=300&fit=crop"
        },
        {
          time: "5:30 PM",
          duration: "45 mins",
          title: "Camel Riding & Henna",
          description: "Traditional camel rides and intricate henna painting",
          tips: "Wear comfortable clothes for camel riding. Henna takes 2-3 hours to develop fully."
        },
        {
          time: "6:15 PM",
          duration: "45 mins",
          title: "Sunset & Photography",
          description: "Watch the spectacular desert sunset with photo opportunities"
        },
        {
          time: "7:30 PM",
          duration: "90 mins",
          title: "BBQ Dinner & Shows",
          description: "Authentic BBQ dinner with belly dancing and Tanoura performances",
          image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop"
        }
      ],
      meals: [
        {
          type: "Dinner",
          description: "Traditional BBQ buffet with vegetarian and non-vegetarian options, Arabic sweets, and unlimited soft drinks"
        }
      ]
    },
    {
      title: "Return Journey",
      duration: "45 minutes",
      locations: ["Desert Camp", "Dubai Hotels"],
      highlights: ["Comfortable return transfer", "Desert night views"],
      description: `Conclude your magical desert experience with a comfortable return journey to your Dubai hotel. Enjoy the serene desert night views and reflect on your unforgettable adventure as our driver safely transports you back to the city.`,
      timeline: [
        {
          time: "9:00 PM",
          duration: "45 mins",
          title: "Hotel Drop-off",
          description: "Safe and comfortable return to your Dubai accommodation",
          tips: "Keep your camera ready for beautiful night desert views during the return journey."
        }
      ]
    }
  ];

  const reviewsData = [
    {
      id: 1,
      userName: "Sarah Johnson",
      userAvatar: "https://randomuser.me/api/portraits/women/32.jpg",
      rating: 5,
      date: "2024-01-15",
      tripType: "Family Trip",
      title: "Absolutely Amazing Desert Experience!",
      content: `This was hands down the highlight of our Dubai trip! The dune bashing was thrilling, the camel ride was so much fun, and the BBQ dinner was delicious. Our guide Ahmed was fantastic - very knowledgeable and made sure everyone felt safe and comfortable. The cultural show was mesmerizing, especially the belly dancing performance. Highly recommend this tour to anyone visiting Dubai!`,
      images: [
        "https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=200&h=200&fit=crop",
        "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=200&h=200&fit=crop"
      ],
      categories: {
        guide: 5,
        value: 5,
        safety: 5,
        experience: 5
      },
      helpfulCount: 24,
      verified: true
    },
    {
      id: 2,
      userName: "Michael Chen",
      userAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
      rating: 4,
      date: "2024-01-10",
      tripType: "Solo Travel",
      title: "Great Adventure with Minor Issues",
      content: `Overall a fantastic experience! The dune bashing was incredible and the sunset views were breathtaking. The food was good, though I wish there were more vegetarian options. The entertainment was enjoyable, but the camp felt a bit crowded. Despite these minor issues, I would definitely recommend this tour. The pickup and drop-off were punctual and the staff was friendly.`,
      categories: {
        guide: 4,
        value: 4,
        safety: 5,
        experience: 4
      },
      helpfulCount: 18,
      verified: true
    },
    {
      id: 3,
      userName: "Emma Rodriguez",
      userAvatar: "https://randomuser.me/api/portraits/women/28.jpg",
      rating: 5,
      date: "2024-01-08",
      tripType: "Couple\'s Trip",
      title: "Perfect Romantic Desert Evening",
      content: `My partner and I had the most romantic evening in the desert! The sunset was absolutely magical, and the traditional camp setting was so authentic. The henna artist was incredibly talented, and we loved trying the shisha. The BBQ dinner was delicious with plenty of options. The belly dancing show was captivating. This tour exceeded all our expectations!`,
      images: [
        "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=200&h=200&fit=crop"
      ],
      categories: {
        guide: 5,
        value: 5,
        safety: 4,
        experience: 5
      },
      helpfulCount: 31,
      verified: true
    }
  ];

  const inclusionsData = [
    {
      id: 1,
      title: "Hotel Pickup & Drop-off",
      description: "Comfortable round-trip transportation from Dubai hotels",
      details: "Available from most Dubai hotels and designated meeting points"
    },
    {
      id: 2,
      title: "Professional Safari Guide",
      description: "Experienced English-speaking guide throughout the tour",
      details: "Multilingual guides available upon request"
    },
    {
      id: 3,
      title: "Dune Bashing Adventure",
      description: "Thrilling 4WD ride across the desert dunes",
      details: "30-45 minutes of exciting dune bashing with photo stops"
    },
    {
      id: 4,
      title: "Camel Riding Experience",
      description: "Traditional camel rides in the desert",
      details: "Short rides suitable for all ages and fitness levels"
    },
    {
      id: 5,
      title: "BBQ Dinner Buffet",
      description: "Delicious barbecue dinner with vegetarian options",
      details: "Includes appetizers, main courses, desserts, and unlimited soft drinks"
    },
    {
      id: 6,
      title: "Cultural Entertainment",
      description: "Live belly dancing and Tanoura dance performances",
      details: "Traditional shows performed by professional artists"
    },
    {
      id: 7,
      title: "Henna Painting",
      description: "Traditional henna designs by skilled artists",
      details: "Complimentary henna painting for hands"
    },
    {
      id: 8,
      title: "Shisha Experience",
      description: "Try traditional Arabic water pipe smoking",
      details: "Various flavors available at the Bedouin camp"
    }
  ];

  const exclusionsData = [
    {
      id: 1,
      title: "Alcoholic Beverages",
      description: "Alcoholic drinks are not included in the package",
      details: "Available for purchase at additional cost"
    },
    {
      id: 2,
      title: "Personal Expenses",
      description: "Souvenirs, additional food, and personal items",
      details: "Shopping opportunities available at the camp"
    },
    {
      id: 3,
      title: "Gratuities",
      description: "Tips for guides and staff are not included",
      details: "Gratuities are appreciated but not mandatory"
    },
    {
      id: 4,
      title: "Travel Insurance",
      description: "Personal travel insurance is not provided",
      details: "Recommended to purchase separately"
    },
    {
      id: 5,
      title: "Quad Biking",
      description: "ATV/Quad bike rides are not included",
      details: "Available as an optional add-on for additional fee"
    }
  ];

  const policiesData = [
    {
      id: 1,
      title: "Cancellation Policy",
      icon: "XCircle",
      items: [
        {
          title: "Free Cancellation",
          description: "Cancel up to 24 hours before the tour starts for a full refund",
          note: "Cancellations within 24 hours are subject to 50% charge"
        },
        {
          title: "No-Show Policy",
          description: "No refund will be provided for no-shows or late arrivals",
          note: "Please arrive at pickup location 10 minutes early"
        }
      ]
    },
    {
      id: 2,
      title: "Health & Safety",
      icon: "Shield",
      items: [
        {
          title: "Medical Conditions",
          description: "Not recommended for pregnant women, people with back problems, or serious medical conditions",
          note: "Please consult your doctor before booking if you have health concerns"
        },
        {
          title: "Age Restrictions",
          description: "Children under 3 years are not permitted for safety reasons",
          note: "Child rates apply for ages 3-11 years"
        }
      ]
    },
    {
      id: 3,
      title: "What to Bring",
      icon: "Package",
      items: [
        {
          title: "Essential Items",
          description: "Sunglasses, sunscreen, comfortable clothing, and closed-toe shoes",
          note: "Avoid wearing white clothing as it gets dusty in the desert"
        },
        {
          title: "Photography",
          description: "Camera or smartphone for capturing memories",
          note: "Professional photography services available at additional cost"
        }
      ]
    }
  ];

  const similarToursData = [
    {
      id: 2,
      name: "Abu Dhabi City Tour with Sheikh Zayed Mosque",
      location: "Abu Dhabi",
      category: "Cultural",
      duration: "8 hours",
      price: 75,
      originalPrice: 95,
      rating: 4.7,
      reviewCount: 1823,
      image: "https://images.unsplash.com/photo-1512632578888-169bbbc64f33?w=400&h=300&fit=crop",
      features: ["Mosque Visit", "City Tour", "Lunch Included"],
      availability: "high",
      instantBooking: true,
      isPopular: true,
      discount: 21
    },
    {
      id: 3,
      name: "Dubai Marina Luxury Yacht Cruise",
      location: "Dubai Marina",
      category: "Luxury",
      duration: "3 hours",
      price: 120,
      rating: 4.9,
      reviewCount: 956,
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop",
      features: ["Luxury Yacht", "Refreshments", "Swimming"],
      availability: "medium",
      instantBooking: true
    },
    {
      id: 4,
      name: "Burj Khalifa & Dubai Mall Experience",
      location: "Downtown Dubai",
      category: "Sightseeing",
      duration: "4 hours",
      price: 65,
      originalPrice: 80,
      rating: 4.6,
      reviewCount: 3241,
      image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=400&h=300&fit=crop",
      features: ["Skip the Line", "Observation Deck", "Mall Tour"],
      availability: "high",
      instantBooking: true,
      discount: 19
    }
  ];

  const navigationSections = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'itinerary', label: 'Itinerary', icon: 'Map' },
    { id: 'inclusions', label: 'Inclusions', icon: 'CheckCircle' },
    { id: 'reviews', label: 'Reviews', icon: 'Star' },
    { id: 'similar', label: 'Similar Tours', icon: 'Compass' }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationSections?.map(section => section?.id);
      const scrollPosition = window.scrollY + 200;

      for (let i = sections?.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections?.[i]);
        if (element && element?.offsetTop <= scrollPosition) {
          setActiveSection(sections?.[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element?.offsetTop - headerOffset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading tour details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <ImageGallery images={tourData?.images} tourName={tourData?.name} />

            {/* Tour Header */}
            <TourHeader tour={tourData} />

            {/* Sticky Navigation */}
            <div className="sticky top-20 z-30 bg-white border border-border rounded-lg p-2 shadow-sm">
              <div className="flex space-x-1 overflow-x-auto">
                {navigationSections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => scrollToSection(section?.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      activeSection === section?.id
                        ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={section?.icon} size={16} />
                    <span>{section?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Section */}
            <div id="overview" className="scroll-mt-32">
              <div className="bg-white rounded-xl p-6 border border-border">
                <h2 className="text-2xl font-bold text-foreground mb-4">Tour Overview</h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Embark on an unforgettable desert adventure that combines thrilling dune bashing, authentic Bedouin culture, and spectacular entertainment. This comprehensive desert safari offers the perfect blend of excitement and cultural immersion, making it one of Dubai's most popular experiences.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your journey begins with a comfortable pickup from your Dubai hotel, followed by an exhilarating dune bashing session across the golden red dunes. Experience the traditional Bedouin lifestyle at our authentic desert camp, complete with camel riding, henna painting, and shisha smoking.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    As the sun sets over the desert, enjoy a delicious BBQ dinner while watching captivating cultural performances including belly dancing and Tanoura shows. This tour provides an authentic taste of Arabian culture combined with modern comfort and safety standards.
                  </p>
                </div>

                {/* Quick Facts */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
                  <div className="text-center">
                    <Icon name="Users" size={24} className="text-primary mx-auto mb-2" />
                    <div className="font-semibold text-foreground">Small Groups</div>
                    <div className="text-sm text-muted-foreground">Max 15 people</div>
                  </div>
                  <div className="text-center">
                    <Icon name="Shield" size={24} className="text-primary mx-auto mb-2" />
                    <div className="font-semibold text-foreground">Safety First</div>
                    <div className="text-sm text-muted-foreground">Certified guides</div>
                  </div>
                  <div className="text-center">
                    <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
                    <div className="font-semibold text-foreground">Full Experience</div>
                    <div className="text-sm text-muted-foreground">6 hours total</div>
                  </div>
                  <div className="text-center">
                    <Icon name="Star" size={24} className="text-primary mx-auto mb-2" />
                    <div className="font-semibold text-foreground">Top Rated</div>
                    <div className="text-sm text-muted-foreground">4.8/5 rating</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Itinerary Section */}
            <div id="itinerary" className="scroll-mt-32">
              <TourItinerary itinerary={itineraryData} />
            </div>

            {/* Inclusions Section */}
            <div id="inclusions" className="scroll-mt-32">
              <TourInclusions 
                inclusions={inclusionsData}
                exclusions={exclusionsData}
                policies={policiesData}
              />
            </div>

            {/* Reviews Section */}
            <div id="reviews" className="scroll-mt-32">
              <ReviewsSection 
                reviews={reviewsData}
                averageRating={tourData?.rating}
                totalReviews={tourData?.reviewCount}
              />
            </div>

            {/* Similar Tours Section */}
            <div id="similar" className="scroll-mt-32">
              <SimilarTours tours={similarToursData} />
            </div>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <BookingWidget tour={tourData} />
          </div>
        </div>
      </div>
      {/* Recent Booking Notifications */}
      <div className="fixed bottom-6 left-6 z-40 space-y-2">
        <div className="bg-white border border-border rounded-lg p-3 shadow-lg animate-slide-up">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm text-foreground">
              <strong>Jessica</strong> from London just booked this tour
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">2 minutes ago</div>
        </div>
      </div>
    </div>
  );
};

export default TourDetail;